import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography,
  Card,
  Row,
  Col,
  Table,
  Button,
  Space,
  Tag,
  Select,
  Input,
  Badge,
  message,
  Alert,
  Tabs,
  Tooltip,
  Progress,
  Statistic,
  Modal,
  Form,
  InputNumber,
  Popconfirm,
  DatePicker,
} from 'antd';
import {
  InboxOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SwapOutlined,
  DownOutlined,
  EyeOutlined,
  StockOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  AlertOutlined,
  EditOutlined,
  PlusOutlined,
  DownloadOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import {
  InventoryDashboard,
  MovementItem,
  DetailedStockItem,
  StockAdjustmentListItem,
  StockTransferListItem,
  inventoryService,
} from '../services/inventoryService';
import { locationService, Location } from '../services/locationService';
import { masterDataService, Product } from '../services/masterDataService';
import { RBACService, PERMISSIONS } from '../utils/rbac';
import { sanitizeAuthToken } from '../services/apiService';
import { exportRowsToExcel } from '../utils/exportToExcel';
import { expiryTagColor, formatShelfDate } from '../utils/shelfLife';
import ModernFormModal from '../components/ModernFormModal';
import { useResponsive } from '../hooks/useResponsive';
import './Inventory.css';

const { Title, Text } = Typography;
const { Option } = Select;

/** Backend returns 401 if this is missing or expired — avoid calling APIs without it. */
function readAuthToken(): string | null {
  return sanitizeAuthToken(localStorage.getItem('authToken'));
}

const Inventory: React.FC = () => {
  const { isMobile } = useResponsive();
  const tableScrollY = isMobile
    ? 'max(220px, calc(100dvh - 520px))'
    : 'max(240px, calc(100dvh - 560px))';
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('stock');
  const [dashboardData, setDashboardData] = useState<InventoryDashboard | null>(null);
  const [stockItems, setStockItems] = useState<DetailedStockItem[]>([]);
  const [recentMovements, setRecentMovements] = useState<MovementItem[]>([]);
  const [lowStockAlerts, setLowStockAlerts] = useState<any[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<number | undefined>();
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText.trim()), 350);
    return () => clearTimeout(t);
  }, [searchText]);

  useEffect(() => {
    setStockPagination((prev) => ({ ...prev, current: 1 }));
  }, [debouncedSearch]);
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [outOfStockOnly, setOutOfStockOnly] = useState(false);
  const [expiryFilter, setExpiryFilter] = useState<'expired' | 'expiring_soon' | null>(null);
  const [stockPagination, setStockPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [movementPagination, setMovementPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [adjustmentPagination, setAdjustmentPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [transferPagination, setTransferPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [adjustments, setAdjustments] = useState<StockAdjustmentListItem[]>([]);
  const [transfers, setTransfers] = useState<StockTransferListItem[]>([]);
  const [adjustmentStatusFilter, setAdjustmentStatusFilter] = useState<'PENDING' | 'APPROVED' | 'CANCELLED' | 'ALL'>('ALL');
  const [approveSubmittingId, setApproveSubmittingId] = useState<number | null>(null);
  const [cancelSubmittingId, setCancelSubmittingId] = useState<number | null>(null);
  const [receiveSubmittingId, setReceiveSubmittingId] = useState<number | null>(null);
  const [deleteSubmittingId, setDeleteSubmittingId] = useState<number | null>(null);
  const [editTransferModalOpen, setEditTransferModalOpen] = useState(false);
  const [editTransferSubmitting, setEditTransferSubmitting] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState<StockTransferListItem | null>(null);
  const [editTransferForm] = Form.useForm();
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailAdjustment, setDetailAdjustment] = useState<StockAdjustmentListItem | null>(null);
  const [detailProductNames, setDetailProductNames] = useState<Record<number, string>>({});
  const [editStockItem, setEditStockItem] = useState<DetailedStockItem | null>(null);
  const [editForm] = Form.useForm();
  const [editSaving, setEditSaving] = useState(false);
  const [adjustmentModalOpen, setAdjustmentModalOpen] = useState(false);
  const [adjustmentSubmitting, setAdjustmentSubmitting] = useState(false);
  const [adjustmentProducts, setAdjustmentProducts] = useState<Product[]>([]);
  const [adjustmentProductsLoading, setAdjustmentProductsLoading] = useState(false);
  const [adjustmentForm] = Form.useForm();
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [transferProducts, setTransferProducts] = useState<Product[]>([]);
  const [transferProductsLoading, setTransferProductsLoading] = useState(false);
  const [transferForm] = Form.useForm();
  const [exportingStock, setExportingStock] = useState(false);
  const canCreateAdjustment = RBACService.hasPermission(PERMISSIONS.STOCK_ADJUSTMENT_CREATE);
  const canCreateTransfer = RBACService.hasPermission(PERMISSIONS.STOCK_TRANSFER_CREATE);
  const canReceiveTransfer = RBACService.hasPermission(PERMISSIONS.STOCK_TRANSFER_RECEIVE);
  const canApproveAdjustment = RBACService.hasPermission(PERMISSIONS.STOCK_ADJUSTMENT_APPROVE);
  const canExportInventory = RBACService.hasPermission(PERMISSIONS.INVENTORY_READ);
  const adjustmentProductId = Form.useWatch('product_id', adjustmentForm);
  const adjustmentLocationId = Form.useWatch('location_id', adjustmentForm);
  const adjustmentType = Form.useWatch('adjustment_type', adjustmentForm);
  const transferFromLocationId = Form.useWatch('from_location_id', transferForm);
  const [adjustmentCurrentQty, setAdjustmentCurrentQty] = useState<number | null>(null);
  const [adjustmentCurrentQtyLoading, setAdjustmentCurrentQtyLoading] = useState(false);
  const currentUser = RBACService.getCurrentUser();
  const currentUserLocationId = Number(currentUser?.locationId);
  const isSuperAdmin = RBACService.isSuperAdmin();
  const isCompanyAdmin = RBACService.isCompanyAdmin();
  /** Match dashboard: company admins and “all locations” roles query company-wide, not only default branch. */
  const seesCompanyWideInventory =
    isCompanyAdmin || RBACService.hasPermission(PERMISSIONS.LOCATIONS_VIEW_ALL);
  const inventoryScopeLocationId = seesCompanyWideInventory ? undefined : selectedLocation;
  const stockFetchSeqRef = useRef(0);
  const movementsFetchSeqRef = useRef(0);
  const alertsFetchSeqRef = useRef(0);
  const scopedLocations = React.useMemo(() => {
    if (isSuperAdmin) return locations;
    const lid = Number(currentUser?.locationId);
    if (!Number.isFinite(lid) || lid <= 0) return [];
    return locations.filter((l) => l.id === lid);
  }, [isSuperAdmin, currentUser?.locationId, locations]);

  const dashboardStockScopeLabel = React.useMemo(() => {
    const info = dashboardData?.location_info;
    if (!info) return '—';
    if (info.scope === 'single_location' || info.location_id != null) {
      return `At ${info.location_name}`;
    }
    return isCompanyAdmin ? 'All company locations' : 'All locations';
  }, [dashboardData?.location_info, isCompanyAdmin]);

  const inventoryLocationDefaultedRef = React.useRef(false);

  // Load locations for dropdown
  useEffect(() => {
    if (!readAuthToken()) return;
    const loadLocations = async () => {
      try {
        const data = await locationService.getLocations({ active_only: true });
        setLocations(data);
      } catch (error) {
        console.error('Error loading locations:', error);
      }
    };
    loadLocations();
  }, []);

  // Default the location filter to the user's assigned branch once (avoids mixing sites).
  useEffect(() => {
    if (locations.length === 0 || inventoryLocationDefaultedRef.current) return;
    inventoryLocationDefaultedRef.current = true;
    const u = RBACService.getCurrentUser();
    if ((u?.roleName || '').toLowerCase().trim() === 'super admin') return;
    const lid = u?.locationId != null ? Number(u.locationId) : NaN;
    if (!Number.isFinite(lid) || lid <= 0) return;
    if (!locations.some((l) => l.id === lid)) return;
    setSelectedLocation(lid);
  }, [locations]);

  /** Products that already have a stock row at this location (same scope as the stock table). */
  const loadAdjustmentProductsForLocation = useCallback(async (locationId: number): Promise<Product[]> => {
    if (!readAuthToken()) return [];
    const byId = new Map<number, Product>();
    let skip = 0;
    const limit = 500;
    for (;;) {
      const res = await inventoryService.getDetailedStock({
        location_id: locationId,
        skip,
        limit,
        include_recent_receipts: false,
      });
      for (const row of res.items) {
        byId.set(row.product.id, {
          id: row.product.id,
          name: row.product.name,
          code: row.product.code,
          is_active: true,
        });
      }
      if (res.items.length < limit || skip + res.items.length >= res.total) break;
      skip += limit;
    }
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  useEffect(() => {
    if (!adjustmentModalOpen) return;
    const lid = Number(adjustmentLocationId);
    if (!lid || !Number.isFinite(lid)) {
      setAdjustmentProducts([]);
      setAdjustmentProductsLoading(false);
      return;
    }
    let cancelled = false;
    setAdjustmentProductsLoading(true);
    loadAdjustmentProductsForLocation(lid)
      .then((list) => {
        if (!cancelled) setAdjustmentProducts(list);
      })
      .catch(() => {
        if (!cancelled) {
          message.error('Failed to load products for this location');
          setAdjustmentProducts([]);
        }
      })
      .finally(() => {
        if (!cancelled) setAdjustmentProductsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [adjustmentModalOpen, adjustmentLocationId, loadAdjustmentProductsForLocation]);

  useEffect(() => {
    if (!transferModalOpen) return;
    const lid = Number(transferFromLocationId);
    if (!lid || !Number.isFinite(lid)) {
      setTransferProducts([]);
      setTransferProductsLoading(false);
      return;
    }
    let cancelled = false;
    setTransferProductsLoading(true);
    loadAdjustmentProductsForLocation(lid)
      .then((list) => {
        if (!cancelled) setTransferProducts(list);
      })
      .catch(() => {
        if (!cancelled) {
          message.error('Failed to load products for source location');
          setTransferProducts([]);
        }
      })
      .finally(() => {
        if (!cancelled) setTransferProductsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [transferModalOpen, transferFromLocationId, loadAdjustmentProductsForLocation]);

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    if (!readAuthToken()) return;
    try {
      // Do not toggle global `loading` here — it races with tab table loaders and causes empty tables / swipe glitches.
      const data = await inventoryService.getDashboard({
        location_id: seesCompanyWideInventory ? undefined : selectedLocation,
      });
      setDashboardData(data);
    } catch (error) {
      message.error('Failed to load inventory dashboard');
      console.error('Error loading dashboard:', error);
    }
  }, [selectedLocation, seesCompanyWideInventory]);

  // Load stock inventory
  const loadStockInventory = useCallback(async () => {
    if (!readAuthToken()) return;
    const seq = ++stockFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await inventoryService.getDetailedStock({
        skip: (stockPagination.current - 1) * stockPagination.pageSize,
        limit: stockPagination.pageSize,
        location_id: inventoryScopeLocationId,
        low_stock_only: lowStockOnly,
        expiry_status: expiryFilter || undefined,
        include_recent_receipts: false,
        search: debouncedSearch || undefined,
      });
      if (seq !== stockFetchSeqRef.current) return;
      setStockItems(Array.isArray(response.items) ? response.items : []);
      setStockPagination(prev => ({ ...prev, total: Number(response.total) || 0 }));
    } catch (error) {
      if (seq !== stockFetchSeqRef.current) return;
      message.error('Failed to load stock inventory');
      console.error('Error loading stock:', error);
    } finally {
      if (seq === stockFetchSeqRef.current) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryScopeLocationId, lowStockOnly, expiryFilter, stockPagination.current, stockPagination.pageSize, debouncedSearch]);

  // Load stock movements
  const loadMovements = useCallback(async () => {
    if (!readAuthToken()) return;
    const seq = ++movementsFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await inventoryService.getDetailedMovements({
        skip: (movementPagination.current - 1) * movementPagination.pageSize,
        limit: movementPagination.pageSize,
        location_id: inventoryScopeLocationId,
      });
      if (seq !== movementsFetchSeqRef.current) return;
      setRecentMovements(response.items ?? []);
      setMovementPagination(prev => ({ ...prev, total: response.total ?? 0 }));
    } catch (error) {
      if (seq !== movementsFetchSeqRef.current) return;
      message.error('Failed to load stock movements');
      console.error('Error loading movements:', error);
    } finally {
      if (seq === movementsFetchSeqRef.current) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryScopeLocationId, movementPagination.current, movementPagination.pageSize]);

  // Load low stock alerts
  const loadLowStockAlerts = useCallback(async () => {
    if (!readAuthToken()) return;
    const seq = ++alertsFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await inventoryService.getLowStockAlerts({
        location_id: inventoryScopeLocationId,
        include_vendors: true,
      });
      if (seq !== alertsFetchSeqRef.current) return;
      setLowStockAlerts(response.alerts || []);
    } catch (error) {
      if (seq !== alertsFetchSeqRef.current) return;
      message.error('Failed to load low stock alerts');
      console.error('Error loading alerts:', error);
    } finally {
      if (seq === alertsFetchSeqRef.current) setLoading(false);
    }
  }, [inventoryScopeLocationId]);

  // Load stock adjustments (pending/approved/cancelled)
  const loadAdjustments = useCallback(async () => {
    if (!readAuthToken()) return;
    try {
      setLoading(true);
      const list = await inventoryService.getStockAdjustments({
        skip: (adjustmentPagination.current - 1) * adjustmentPagination.pageSize,
        limit: adjustmentPagination.pageSize,
        // Company Admin approval queue: show all company locations unless explicitly filtered later.
        location_id: seesCompanyWideInventory ? undefined : selectedLocation,
        status: adjustmentStatusFilter === 'ALL' ? undefined : adjustmentStatusFilter,
      });
      setAdjustments(list || []);
      // Backend list endpoint returns array, so estimate total from current page size.
      setAdjustmentPagination((prev) => ({
        ...prev,
        total:
          list.length < prev.pageSize
            ? (prev.current - 1) * prev.pageSize + list.length
            : prev.current * prev.pageSize + (list.length === prev.pageSize ? 1 : 0),
      }));
    } catch (error) {
      message.error('Failed to load stock adjustments');
      console.error('Error loading adjustments:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedLocation, seesCompanyWideInventory, adjustmentPagination.current, adjustmentPagination.pageSize, adjustmentStatusFilter]);

  const loadTransfers = useCallback(async () => {
    if (!readAuthToken()) return;
    try {
      setLoading(true);
      // Do not pass from_location_id for company-wide roles: that hid inbound transfers
      // and only showed outgoing from one branch. Backend scopes by company / user location.
      const list = await inventoryService.getStockTransfers({
        skip: (transferPagination.current - 1) * transferPagination.pageSize,
        limit: transferPagination.pageSize,
      });
      setTransfers(list || []);
      setTransferPagination((prev) => ({
        ...prev,
        total:
          list.length < prev.pageSize
            ? (prev.current - 1) * prev.pageSize + list.length
            : prev.current * prev.pageSize + (list.length === prev.pageSize ? 1 : 0),
      }));
    } catch (error) {
      message.error('Failed to load stock transfers');
      console.error('Error loading transfers:', error);
    } finally {
      setLoading(false);
    }
  }, [transferPagination.current, transferPagination.pageSize]);

  // Load data based on active tab
  useEffect(() => {
    loadDashboardData();
    if (activeTab === 'stock') {
      loadStockInventory();
    } else if (activeTab === 'movements') {
      loadMovements();
    } else if (activeTab === 'adjustments') {
      loadAdjustments();
    } else if (activeTab === 'transfers') {
      loadTransfers();
    } else if (activeTab === 'alerts') {
      loadLowStockAlerts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, loadDashboardData, loadStockInventory, loadMovements, loadAdjustments, loadTransfers, loadLowStockAlerts]);

  /** Reset page when switching tabs so we never show an empty table on a stale page index. */
  const onInventoryTabChange = (key: string) => {
    if (key !== activeTab) {
      if (key === 'stock') setStockPagination((p) => ({ ...p, current: 1 }));
      if (key === 'movements') setMovementPagination((p) => ({ ...p, current: 1 }));
      if (key === 'adjustments') setAdjustmentPagination((p) => ({ ...p, current: 1 }));
      if (key === 'transfers') setTransferPagination((p) => ({ ...p, current: 1 }));
    }
    setActiveTab(key);
  };

  const refreshData = () => {
    if (!readAuthToken()) return;
    loadDashboardData();
    if (activeTab === 'stock') {
      loadStockInventory();
    } else if (activeTab === 'movements') {
      loadMovements();
    } else if (activeTab === 'adjustments') {
      loadAdjustments();
    } else if (activeTab === 'transfers') {
      loadTransfers();
    } else if (activeTab === 'alerts') {
      loadLowStockAlerts();
    }
  };

  // Auto-refresh inventory data every 30-60 seconds (randomized interval).
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const scheduleNext = () => {
      if (cancelled) return;
      const delayMs = 30000 + Math.floor(Math.random() * 30001); // 30s..60s
      timer = setTimeout(async () => {
        if (cancelled) return;
        if (!readAuthToken()) {
          scheduleNext();
          return;
        }
        refreshData();
        scheduleNext();
      }, delayMs);
    };

    scheduleNext();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    selectedLocation,
    lowStockOnly,
    outOfStockOnly,
    expiryFilter,
    debouncedSearch,
    stockPagination.current,
    stockPagination.pageSize,
    movementPagination.current,
    movementPagination.pageSize,
    adjustmentStatusFilter,
    adjustmentPagination.current,
    adjustmentPagination.pageSize,
    transferPagination.current,
    transferPagination.pageSize,
    isCompanyAdmin,
    seesCompanyWideInventory,
    inventoryScopeLocationId,
  ]);

  // Stock Inventory Columns
  const stockColumns: ColumnsType<DetailedStockItem> = [
    {
      title: 'Product',
      key: 'product',
      width: 250,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.product.name}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>Code: {record.product.code}</div>
          {record.product.category && (
            <Tag style={{ marginTop: 4 }}>{record.product.category}</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      width: 150,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.location.name}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>{record.location.code}</div>
        </div>
      ),
    },
    {
      title: 'On Hand',
      dataIndex: ['quantities', 'on_hand'],
      key: 'on_hand',
      align: 'right' as const,
      width: 100,
      render: (qty: number) => (
        <span style={{ fontWeight: 500, fontSize: '15px' }}>{qty.toLocaleString()}</span>
      ),
    },
    {
      title: 'Available',
      dataIndex: ['quantities', 'available'],
      key: 'available',
      align: 'right' as const,
      width: 120,
      render: (qty: number, record) => {
        const isLow = record.stock_status === 'Low Stock';
        return (
          <span style={{
            color: isLow ? '#ff4d4f' : '#52c41a',
            fontWeight: 'bold',
            fontSize: '15px'
          }}>
            {qty.toLocaleString()}
          </span>
        );
      },
    },
    {
      title: 'Barcode',
      dataIndex: ['product', 'barcode'],
      key: 'barcode',
      width: 120,
      render: (barcode: string) => barcode || '—',
    },
    {
      title: 'Cost Price',
      dataIndex: ['product', 'cost_price'],
      key: 'cost_price',
      align: 'right' as const,
      width: 100,
      render: (val: number) => val != null ? `Nu. ${Number(val).toLocaleString()}` : '—',
    },
    {
      title: 'Selling Price',
      dataIndex: ['product', 'selling_price'],
      key: 'selling_price',
      align: 'right' as const,
      width: 110,
      render: (val: number) => val != null ? `Nu. ${Number(val).toLocaleString()}` : '—',
    },
    {
      title: 'Status',
      dataIndex: 'stock_status',
      key: 'status',
      width: 120,
      render: (status: string, record) => {
        const isLow = status === 'Low Stock';
        const available = record.quantities.available;
        const reorderLevel = record.product.reorder_level || 0;
        const percentage = reorderLevel > 0 ? (available / reorderLevel) * 100 : 100;
        
        return (
          <div>
            <Tag color={isLow ? 'red' : 'green'} icon={isLow ? <WarningOutlined /> : <CheckCircleOutlined />}>
              {status}
            </Tag>
            {isLow && (
              <Progress
                percent={Math.min(percentage, 100)}
                size="small"
                status="exception"
                style={{ marginTop: 4 }}
              />
            )}
          </div>
        );
      },
    },
    {
      title: 'Expiry',
      key: 'expiry',
      width: 160,
      render: (_, record) => (
        <div>
          <Tag color={expiryTagColor(record.expiry_status)}>{record.expiry_label || 'No expiry date'}</Tag>
          {record.expiry_date ? (
            <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
              Mfg {formatShelfDate(record.manufacture_date)}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Last Updated',
      dataIndex: 'last_updated',
      key: 'last_updated',
      width: 150,
      render: (date: string) => moment(date).format('MMM DD, YYYY HH:mm'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: DetailedStockItem) => (
        <Tooltip title="Edit selling price">
          <Button
            aria-label="Edit"
            className="icon-btn icon-btn--primary"
            shape="round"
            icon={<EditOutlined />}
            onClick={() => {
              setEditStockItem(record);
              editForm.setFieldsValue({ selling_price: record.product.selling_price ?? 0 });
            }}
          />
        </Tooltip>
      ),
    },
  ];

  const handleSaveSellingPrice = async () => {
    if (!editStockItem) return;
    try {
      const values = await editForm.validateFields();
      setEditSaving(true);
      await masterDataService.updateProduct(editStockItem.product.id, {
        selling_price: values.selling_price,
      });
      message.success('Selling price updated');
      setEditStockItem(null);
      loadStockInventory();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Failed to update');
    } finally {
      setEditSaving(false);
    }
  };

  const exportStockToExcel = async () => {
    if (!readAuthToken()) {
      message.error('Your session has expired. Please log in again.');
      return;
    }
    try {
      setExportingStock(true);
      const CHUNK = 500;
      let skip = 0;
      const allItems: DetailedStockItem[] = [];
      for (;;) {
        const response = await inventoryService.getDetailedStock({
          skip,
          limit: CHUNK,
          location_id: inventoryScopeLocationId,
          low_stock_only: lowStockOnly,
          expiry_status: expiryFilter || undefined,
          include_recent_receipts: false,
          search: debouncedSearch || undefined,
        });
        const items = response.items || [];
        allItems.push(...items);
        if (items.length < CHUNK) break;
        skip += items.length;
      }

      const rows = allItems.filter((item) => {
        if (outOfStockOnly) return item.quantities.available <= 0;
        return true;
      });

      if (rows.length === 0) {
        message.info('No inventory rows to export');
        return;
      }

      exportRowsToExcel(
        rows,
        [
          { header: 'Product', value: (item) => item.product.name },
          { header: 'Code', value: (item) => item.product.code },
          { header: 'Category', value: (item) => item.product.category || '' },
          { header: 'Location', value: (item) => item.location.name },
          { header: 'Location Code', value: (item) => item.location.code },
          { header: 'On Hand', value: (item) => item.quantities.on_hand },
          { header: 'Committed', value: (item) => item.quantities.committed },
          { header: 'Available', value: (item) => item.quantities.available },
          { header: 'Barcode', value: (item) => item.product.barcode || '' },
          { header: 'Cost Price', value: (item) => item.product.cost_price ?? '' },
          { header: 'Selling Price', value: (item) => item.product.selling_price ?? '' },
          { header: 'Reorder Level', value: (item) => item.product.reorder_level ?? '' },
          { header: 'Unit of Measure', value: (item) => item.product.unit_of_measure || '' },
          { header: 'Status', value: (item) => item.stock_status },
          { header: 'Manufacture date', value: (item) => item.manufacture_date || '' },
          { header: 'Expiry date', value: (item) => item.expiry_date || '' },
          { header: 'Expiry status', value: (item) => item.expiry_label || '' },
          {
            header: 'Last Updated',
            value: (item) => (item.last_updated ? moment(item.last_updated).format('YYYY-MM-DD HH:mm') : ''),
          },
        ],
        `inventory_stock_${moment().format('YYYYMMDD_HHmmss')}.xlsx`,
        'Inventory',
      );
      message.success(`Exported ${rows.length} inventory row(s) to Excel`);
    } catch (error) {
      console.error('Inventory export failed:', error);
      message.error('Failed to export inventory');
    } finally {
      setExportingStock(false);
    }
  };

  const getCurrentQuantity = (productId: number, locationId: number): number => {
    const row = stockItems.find((s) => s.product.id === productId && s.location.id === locationId);
    return row?.quantities?.on_hand ?? 0;
  };

  const loadAdjustmentCurrentQty = useCallback(async (productId: number, locationId: number) => {
    if (!readAuthToken()) {
      setAdjustmentCurrentQty(getCurrentQuantity(productId, locationId));
      return;
    }
    try {
      setAdjustmentCurrentQtyLoading(true);
      const response = await inventoryService.getDetailedStock({
        skip: 0,
        limit: 1,
        product_id: productId,
        location_id: locationId,
      });
      const exactQty = response?.items?.[0]?.quantities?.on_hand;
      if (typeof exactQty === 'number') {
        setAdjustmentCurrentQty(exactQty);
      } else {
        setAdjustmentCurrentQty(0);
      }
    } catch (error) {
      console.error('Failed to fetch current stock for adjustment:', error);
      setAdjustmentCurrentQty(getCurrentQuantity(productId, locationId));
    } finally {
      setAdjustmentCurrentQtyLoading(false);
    }
  }, [stockItems]);

  const openAdjustmentModal = () => {
    adjustmentForm.resetFields();
    setAdjustmentProducts([]);
    adjustmentForm.setFieldsValue({
      adjustment_type: 'IN',
      quantity: 1,
      location_id: selectedLocation ?? undefined,
    });
    setAdjustmentCurrentQty(null);
    setAdjustmentCurrentQtyLoading(false);
    setAdjustmentModalOpen(true);
  };

  const handleCreateAdjustment = async () => {
    if (!readAuthToken()) {
      message.error('Your session has expired. Please log in again.');
      return;
    }
    try {
      const values = await adjustmentForm.validateFields();
      const productId = Number(values.product_id);
      const locationId = Number(values.location_id);
      const qty = Number(values.quantity || 0);
      const type: 'IN' | 'OUT' = values.adjustment_type;
      const reason = String(values.reason || '').trim();
      const notes = String(values.notes || '').trim();

      const currentQty = adjustmentCurrentQty ?? getCurrentQuantity(productId, locationId);
      const adjustedQty = type === 'IN' ? currentQty + qty : currentQty - qty;
      if (type === 'OUT' && currentQty <= 0) {
        message.error('Current available stock is 0. OUT adjustment is not allowed.');
        return;
      }
      if (adjustedQty < 0) {
        message.error(`Cannot adjust OUT by ${qty}. Current stock is ${currentQty}.`);
        return;
      }

      setAdjustmentSubmitting(true);
      if (type === 'IN' && (!values.manufacture_date || !values.expiry_date)) {
        message.error('Enter manufacture and expiry dates when adding stock');
        return;
      }
      if (
        type === 'IN' &&
        values.manufacture_date &&
        values.expiry_date &&
        !moment(values.expiry_date).isAfter(moment(values.manufacture_date), 'day')
      ) {
        message.error('Expiry must be after the manufacture date');
        return;
      }

      const payload = {
        location_id: locationId,
        adjustment_date: moment().format('YYYY-MM-DD'),
        reason,
        notes: notes || undefined,
        items: [
          {
            product_id: productId,
            current_quantity: currentQty,
            adjusted_quantity: adjustedQty,
            reason,
            ...(type === 'IN'
              ? {
                  manufacture_date: moment(values.manufacture_date).format('YYYY-MM-DD'),
                  expiry_date: moment(values.expiry_date).format('YYYY-MM-DD'),
                }
              : {}),
          },
        ],
      };

      const res = await inventoryService.createStockAdjustment(payload);
      message.success(res?.adjustment_number ? `Adjustment created: ${res.adjustment_number}` : 'Stock adjustment created');
      setAdjustmentModalOpen(false);
      adjustmentForm.resetFields();
      loadDashboardData();
      loadStockInventory();
      if (activeTab !== 'movements') {
        onInventoryTabChange('movements');
      } else {
        loadMovements();
      }
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || e?.message || 'Failed to create stock adjustment');
    } finally {
      setAdjustmentSubmitting(false);
    }
  };

  useEffect(() => {
    if (!adjustmentModalOpen) return;
    const productId = Number(adjustmentProductId);
    const locationId = Number(adjustmentLocationId);
    if (!productId || !locationId) {
      setAdjustmentCurrentQty(null);
      return;
    }
    loadAdjustmentCurrentQty(productId, locationId);
  }, [adjustmentModalOpen, adjustmentProductId, adjustmentLocationId, loadAdjustmentCurrentQty]);

  // Movement Columns
  const movementColumns: ColumnsType<MovementItem> = [
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date: string) => (
        <div>
          <div>{moment(date).format('MMM DD')}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>{moment(date).format('HH:mm')}</div>
        </div>
      ),
    },
    {
      title: 'Product',
      key: 'product',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.product.name}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>{record.product.code}</div>
        </div>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      width: 150,
      render: (_, record) => record.location.name,
    },
    {
      title: 'Type',
      dataIndex: ['movement_details', 'type'],
      key: 'type',
      width: 100,
      render: (type: string) => {
        const isIn = type === 'IN';
        return (
          <Tag color={isIn ? 'green' : 'red'} icon={isIn ? <ArrowUpOutlined /> : <ArrowDownOutlined />}>
            {type}
          </Tag>
        );
      },
    },
    {
      title: 'Quantity',
      dataIndex: ['movement_details', 'quantity'],
      key: 'quantity',
      align: 'right' as const,
      width: 100,
      render: (qty: number) => (
        <span style={{ color: qty > 0 ? '#52c41a' : '#ff4d4f', fontWeight: 500 }}>
          {qty > 0 ? '+' : ''}{qty.toLocaleString()}
        </span>
      ),
    },
    {
      title: 'Reference',
      dataIndex: ['movement_details', 'reference_type'],
      key: 'reference',
      width: 150,
      render: (refType: string, record) => {
        const refDetails = record.reference_details;
        if (refDetails) {
          if (refDetails.grpo_number) {
            return <Tooltip title={`PO: ${refDetails.po_number}`}><Tag color="blue">GRPO: {refDetails.grpo_number}</Tag></Tooltip>;
          } else if (refDetails.adjustment_number) {
            return <Tag color="purple">{refDetails.adjustment_number}</Tag>;
          } else if (refDetails.transfer_number) {
            return <Tag color="orange">{refDetails.transfer_number}</Tag>;
          }
        }
        return <Tag color="blue">{refType.replace('_', ' ')}</Tag>;
      },
    },
    {
      title: 'Created By',
      dataIndex: ['created_by', 'name'],
      key: 'created_by',
      width: 120,
    },
  ];

  const handleApproveAdjustment = async (row: StockAdjustmentListItem) => {
    try {
      setApproveSubmittingId(row.id);
      const res = await inventoryService.approveStockAdjustment(row.id);
      message.success(res?.adjustment_number ? `Approved: ${res.adjustment_number}` : 'Adjustment approved');
      loadAdjustments();
      loadDashboardData();
      loadStockInventory();
      loadMovements();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || 'Failed to approve adjustment');
    } finally {
      setApproveSubmittingId(null);
    }
  };

  const handleCancelAdjustment = async (row: StockAdjustmentListItem) => {
    try {
      setCancelSubmittingId(row.id);
      const res = await inventoryService.cancelStockAdjustment(row.id);
      message.success(res?.adjustment_number ? `Cancelled: ${res.adjustment_number}` : 'Adjustment cancelled');
      loadAdjustments();
      loadDashboardData();
      loadStockInventory();
      loadMovements();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || 'Failed to cancel adjustment');
    } finally {
      setCancelSubmittingId(null);
    }
  };

  const openAdjustmentDetail = async (row: StockAdjustmentListItem) => {
    setDetailAdjustment(row);
    setDetailProductNames({});
    setDetailModalOpen(true);
    const itemIds = Array.from(
      new Set((row.adjustment_items || []).map((it) => Number(it.product_id)).filter((id) => Number.isFinite(id) && id > 0)),
    );
    const missing = itemIds.filter((id) => !(row.adjustment_items || []).some((it) => it.product_id === id && !!it.product?.name));
    if (missing.length === 0) return;
    try {
      const results = await Promise.all(
        missing.map(async (id) => {
          try {
            const p = await masterDataService.getProduct(id);
            return [id, p?.name || `Product #${id}`] as const;
          } catch {
            return [id, `Product #${id}`] as const;
          }
        }),
      );
      const map: Record<number, string> = {};
      results.forEach(([id, name]) => {
        map[id] = name;
      });
      setDetailProductNames(map);
    } catch {
      // Keep ID fallback labels.
    }
  };

  const adjustmentColumns: ColumnsType<StockAdjustmentListItem> = [
    {
      title: 'Adjustment #',
      dataIndex: 'adjustment_number',
      key: 'adjustment_number',
      width: 160,
      render: (v: string) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: 'Date',
      dataIndex: 'adjustment_date',
      key: 'adjustment_date',
      width: 120,
      render: (v: string) => (v ? moment(v).format('MMM DD, YYYY') : '—'),
    },
    {
      title: 'Location',
      key: 'location',
      width: 140,
      render: (_, r) => locations.find((l) => l.id === r.location_id)?.name || `#${r.location_id}`,
    },
    {
      title: 'Items',
      key: 'items',
      width: 320,
      render: (_, r) => {
        const items = r.adjustment_items || [];
        if (items.length === 0) return '—';
        return (
          <div className="adj-item-cell">
            {items.slice(0, 2).map((it) => (
              <div key={it.id || `${r.id}-${it.product_id}`} style={{ fontSize: 12, marginBottom: 4 }}>
                <div>{(it.product?.name || `Product #${it.product_id}`)}: {it.current_quantity} → {it.adjusted_quantity}</div>
                <span className={`adj-diff-chip ${it.difference >= 0 ? 'adj-diff-chip--plus' : 'adj-diff-chip--minus'}`}>
                  ({it.difference >= 0 ? '+' : ''}{it.difference})
                </span>
              </div>
            ))}
            {items.length > 2 && <Text type="secondary">+{items.length - 2} more</Text>}
          </div>
        );
      },
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      width: 220,
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: StockAdjustmentListItem['status']) => {
        const color = status === 'APPROVED' ? 'green' : status === 'PENDING' ? 'gold' : 'red';
        return <Tag color={color} style={{ borderRadius: 999, fontWeight: 600 }}>{status}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_, row) => (
        <Space>
          {canApproveAdjustment && row.status === 'PENDING' && (
            <>
              <Button
                type="primary"
                size="small"
                loading={approveSubmittingId === row.id}
                onClick={() => handleApproveAdjustment(row)}
              >
                Approve
              </Button>
              <Popconfirm
                title="Cancel this adjustment? This will reject the pending request."
                okText="Yes, cancel"
                cancelText="No"
                onConfirm={() => handleCancelAdjustment(row)}
              >
                <Button
                  danger
                  size="small"
                  loading={cancelSubmittingId === row.id}
                >
                  Cancel
                </Button>
              </Popconfirm>
            </>
          )}
          {row.status !== 'PENDING' && (
            <Button icon={<EyeOutlined />} size="small" onClick={() => openAdjustmentDetail(row)}>
              View
            </Button>
          )}
          {!canApproveAdjustment && row.status === 'PENDING' && (
            <Button icon={<EyeOutlined />} size="small" onClick={() => openAdjustmentDetail(row)}>
              Review
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const adjustmentStats = React.useMemo(() => {
    const total = adjustments.length;
    const approved = adjustments.filter((a) => a.status === 'APPROVED').length;
    const pending = adjustments.filter((a) => a.status === 'PENDING').length;
    const cancelled = adjustments.filter((a) => a.status === 'CANCELLED').length;
    return { total, approved, pending, cancelled };
  }, [adjustments]);

  const openTransferModal = () => {
    transferForm.resetFields();
    setTransferProducts([]);
    transferForm.setFieldsValue({
      transfer_type: 'BRANCH_TRANSFER',
      from_location_id: selectedLocation ?? undefined,
      lines: [{ quantity: 1 }],
    });
    setTransferModalOpen(true);
  };

  const handleCreateTransfer = async () => {
    if (!readAuthToken()) {
      message.error('Your session has expired. Please log in again.');
      return;
    }
    try {
      const values = await transferForm.validateFields();
      const fromLocationId = Number(values.from_location_id);
      const toLocationId = Number(values.to_location_id);
      const rawLines = (values.lines || []) as Array<{
        product_id?: number | string;
        quantity?: number;
        line_notes?: string;
      }>;
      const lines = rawLines.filter(
        (l) => l != null && l.product_id != null && l.product_id !== '',
      );
      if (lines.length === 0) {
        message.error('Add at least one product line.');
        return;
      }
      if (fromLocationId === toLocationId) {
        message.error('Source and destination locations must be different.');
        return;
      }
      const productIds = lines.map((l) => Number(l.product_id));
      if (productIds.some((id) => !Number.isFinite(id) || id < 1)) {
        message.error('Each line needs a valid product.');
        return;
      }
      if (new Set(productIds).size !== productIds.length) {
        message.error('Each product can only appear once. Combine quantities or remove a duplicate row.');
        return;
      }
      for (const l of lines) {
        const qty = Number(l.quantity ?? 0);
        if (!Number.isFinite(qty) || qty < 1) {
          message.error('Each line needs a quantity of at least 1.');
          return;
        }
      }
      const totalByProduct = new Map<number, number>();
      for (const l of lines) {
        const pid = Number(l.product_id);
        const q = Number(l.quantity ?? 0);
        totalByProduct.set(pid, (totalByProduct.get(pid) || 0) + q);
      }
      const totalsEntries = Array.from(totalByProduct.entries());
      for (let i = 0; i < totalsEntries.length; i += 1) {
        const [pid, total] = totalsEntries[i];
        const available = getCurrentQuantity(pid, fromLocationId);
        if (total > available) {
          const label = transferProducts.find((p) => p.id === pid);
          const name = label ? `${label.name} (${label.code})` : `Product #${pid}`;
          message.error(`Insufficient stock for ${name}. Need ${total}, available ${available}.`);
          return;
        }
      }
      setTransferSubmitting(true);
      const payload = {
        from_location_id: fromLocationId,
        to_location_id: toLocationId,
        transfer_date: moment().format('YYYY-MM-DD'),
        transfer_type: values.transfer_type,
        notes: String(values.notes || '').trim() || undefined,
        items: lines.map((l) => ({
          product_id: Number(l.product_id),
          quantity: Number(l.quantity),
          notes: String(l.line_notes || '').trim() || undefined,
        })),
      };
      const res = await inventoryService.createStockTransfer(payload);
      message.success(res?.transfer_number ? `Transfer created: ${res.transfer_number}` : 'Stock transfer created');
      setTransferModalOpen(false);
      transferForm.resetFields();
      loadDashboardData();
      loadStockInventory();
      loadMovements();
      onInventoryTabChange('transfers');
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || e?.message || 'Failed to create stock transfer');
    } finally {
      setTransferSubmitting(false);
    }
  };

  const handleReceiveTransfer = async (row: StockTransferListItem) => {
    try {
      setReceiveSubmittingId(row.id);
      const receivedItems = Object.fromEntries(
        (row.transfer_items || []).map((it) => [Number(it.product_id), Number(it.quantity || 0)]),
      );
      const res = await inventoryService.receiveStockTransfer(row.id, receivedItems);
      message.success(res?.transfer_number ? `Received: ${res.transfer_number}` : 'Transfer received');
      loadTransfers();
      loadDashboardData();
      loadStockInventory();
      loadMovements();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || 'Failed to receive transfer');
    } finally {
      setReceiveSubmittingId(null);
    }
  };

  const openEditTransferModal = (row: StockTransferListItem) => {
    setEditingTransfer(row);
    editTransferForm.setFieldsValue({
      notes: row.notes || '',
      transfer_lines: (row.transfer_items || []).map((it) => ({
        item_id: it.id,
        product_id: it.product_id,
        product_label: it.product?.name
          ? `${it.product.name} (${it.product.code || it.product_id})`
          : `Product #${it.product_id}`,
        quantity: it.quantity,
      })),
    });
    setEditTransferModalOpen(true);
  };

  const handleSaveTransferEdit = async () => {
    if (!editingTransfer) return;
    try {
      const values = await editTransferForm.validateFields();
      setEditTransferSubmitting(true);
      const lines = (values.transfer_lines || []) as Array<{
        item_id: number;
        quantity: number;
      }>;
      await inventoryService.updateStockTransfer(editingTransfer.id, {
        notes: String(values.notes || '').trim() || undefined,
        items: lines.map((l) => ({
          item_id: Number(l.item_id),
          quantity: Number(l.quantity),
        })),
      });
      message.success('Transfer updated');
      setEditTransferModalOpen(false);
      setEditingTransfer(null);
      loadTransfers();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || e?.message || 'Failed to update transfer');
    } finally {
      setEditTransferSubmitting(false);
    }
  };

  const handleDeleteTransfer = async (row: StockTransferListItem) => {
    if (row.status === 'RECEIVED') {
      message.warning('Received transfers are locked and cannot be deleted.');
      return;
    }
    try {
      setDeleteSubmittingId(row.id);
      const res = await inventoryService.deleteStockTransfer(row.id);
      message.success(res?.message || 'Transfer deleted');
      loadTransfers();
      loadDashboardData();
      loadStockInventory();
      loadMovements();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || 'Failed to delete transfer');
    } finally {
      setDeleteSubmittingId(null);
    }
  };

  const transferColumns: ColumnsType<StockTransferListItem> = [
    {
      title: 'Transfer #',
      dataIndex: 'transfer_number',
      key: 'transfer_number',
      width: 160,
      render: (v: string) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: 'Date',
      dataIndex: 'transfer_date',
      key: 'transfer_date',
      width: 120,
      render: (v: string) => (v ? moment(v).format('MMM DD, YYYY') : '—'),
    },
    {
      title: 'From',
      key: 'from',
      width: 140,
      render: (_, r) => locations.find((l) => l.id === r.from_location_id)?.name || `#${r.from_location_id}`,
    },
    {
      title: 'To',
      key: 'to',
      width: 140,
      render: (_, r) => locations.find((l) => l.id === r.to_location_id)?.name || `#${r.to_location_id}`,
    },
    {
      title: 'Items',
      key: 'items',
      width: 260,
      render: (_, r) => {
        const items = r.transfer_items || [];
        if (items.length === 0) return '—';
        return (
          <div>
            {items.slice(0, 2).map((it) => (
              <div key={it.id || `${r.id}-${it.product_id}`} style={{ fontSize: 12, marginBottom: 4 }}>
                Product #{it.product_id}: {it.quantity}
                {r.status === 'RECEIVED' ? ` (received ${it.received_quantity})` : ''}
              </div>
            ))}
            {items.length > 2 && <Text type="secondary">+{items.length - 2} more</Text>}
          </div>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'transfer_type',
      key: 'transfer_type',
      width: 150,
      render: (v: string) => <Tag color="purple">{String(v || '').replace('_', ' ')}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: StockTransferListItem['status']) => {
        const color = status === 'RECEIVED' ? 'green' : status === 'IN_TRANSIT' ? 'gold' : 'default';
        return <Tag color={color} style={{ borderRadius: 999, fontWeight: 600 }}>{status}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_, row) => (
        <Space>
          {canReceiveTransfer && row.status === 'IN_TRANSIT' && Number.isFinite(currentUserLocationId) && currentUserLocationId === row.to_location_id && (
            <Popconfirm
              title="Receive this transfer at destination?"
              okText="Receive"
              cancelText="Cancel"
              onConfirm={() => handleReceiveTransfer(row)}
            >
              <Button
                type="primary"
                size="small"
                loading={receiveSubmittingId === row.id}
              >
                Receive
              </Button>
            </Popconfirm>
          )}
          {Number.isFinite(currentUserLocationId) && currentUserLocationId === row.from_location_id && (
            <>
              <Button
                size="small"
                onClick={() => openEditTransferModal(row)}
                disabled={row.status === 'RECEIVED'}
              >
                Edit
              </Button>
              <Popconfirm
                title={row.status === 'RECEIVED' ? 'Received transfers cannot be deleted.' : 'Delete this transfer?'}
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true, disabled: row.status === 'RECEIVED' }}
                onConfirm={() => handleDeleteTransfer(row)}
              >
                <Button
                  danger
                  size="small"
                  loading={deleteSubmittingId === row.id}
                  disabled={row.status === 'RECEIVED'}
                >
                  Delete
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  // Low Stock Alerts Columns
  const alertColumns: ColumnsType<any> = [
    {
      title: 'Product',
      key: 'product',
      width: 250,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.product.name}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>Code: {record.product.code}</div>
        </div>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      width: 150,
      render: (_, record) => record.location.name,
    },
    {
      title: 'Current Stock',
      dataIndex: ['stock_info', 'current_stock'],
      key: 'current_stock',
      align: 'right' as const,
      width: 120,
      render: (qty: number) => (
        <span style={{ fontWeight: 'bold', color: '#ff4d4f' }}>{qty.toLocaleString()}</span>
      ),
    },
    {
      title: 'Reorder Level',
      dataIndex: ['stock_info', 'reorder_level'],
      key: 'reorder_level',
      align: 'right' as const,
      width: 120,
      render: (level: number) => level.toLocaleString(),
    },
    {
      title: 'Shortage',
      dataIndex: ['stock_info', 'shortage'],
      key: 'shortage',
      align: 'right' as const,
      width: 120,
      render: (shortage: number) => (
        <Badge count={shortage} style={{ backgroundColor: '#ff4d4f' }} />
      ),
    },
    {
      title: 'Urgency',
      dataIndex: 'urgency',
      key: 'urgency',
      width: 120,
      render: (urgency: string) => {
        const colors: Record<string, string> = {
          Critical: 'red',
          High: 'orange',
          Medium: 'gold',
        };
        return <Tag color={colors[urgency]}>{urgency}</Tag>;
      },
    },
    {
      title: 'Preferred Vendor',
      dataIndex: ['preferred_vendor', 'name'],
      key: 'vendor',
      width: 200,
      render: (vendorName: string, record) => {
        if (record.preferred_vendor) {
          return (
            <div>
              <div style={{ fontWeight: 500 }}>{record.preferred_vendor.name}</div>
              <div style={{ fontSize: '12px', color: '#999' }}>
                {record.preferred_vendor.phone} | Lead: {record.preferred_vendor.lead_time_days} days
              </div>
            </div>
          );
        }
        return <Text type="secondary">No vendor</Text>;
      },
    },
  ];

  // Handle card clicks to filter data
  const handleLowStockClick = () => {
    setLowStockOnly(true);
    setOutOfStockOnly(false);
    setExpiryFilter(null);
    setActiveTab('stock');
    setStockPagination(prev => ({ ...prev, current: 1 }));
    setSearchText('');
  };

  const handleOutOfStockClick = () => {
    setLowStockOnly(false);
    setOutOfStockOnly(true);
    setExpiryFilter(null);
    setActiveTab('stock');
    setStockPagination(prev => ({ ...prev, current: 1 }));
    setSearchText('');
  };

  const handleTotalProductsClick = () => {
    setLowStockOnly(false);
    setOutOfStockOnly(false);
    setExpiryFilter(null);
    setActiveTab('stock');
    setStockPagination(prev => ({ ...prev, current: 1 }));
    setSearchText('');
  };

  const handleExpiryClick = (kind: 'expired' | 'expiring_soon') => {
    setLowStockOnly(false);
    setOutOfStockOnly(false);
    setExpiryFilter(kind);
    setActiveTab('stock');
    setStockPagination(prev => ({ ...prev, current: 1 }));
    setSearchText('');
  };

  // Filter stock items (search is server-side). Client-only: out-of-stock card filter.
  const filteredStockItems = React.useMemo(() => {
    return stockItems.filter(item => {
      // Filter for out of stock items if that filter is active
      if (outOfStockOnly) {
        return item.quantities.available <= 0;
      }
      
      // Filter for low stock items if that filter is active
      // Low stock means available quantity is less than or equal to reorder level
      // Also check the stock_status field which the backend sets
      if (lowStockOnly) {
        // First check if backend already marked it as low stock
        if (item.stock_status === 'Low Stock') {
          return true;
        }
        
        // Otherwise, check based on reorder level
        const reorderLevel = item.product.reorder_level;
        const available = item.quantities.available;
        
        if (reorderLevel !== null && reorderLevel !== undefined && reorderLevel > 0) {
          // Use reorder level if it's set
          return available <= reorderLevel;
        } else {
          // If no reorder level is set, consider items with available <= 0 as low stock
          return available <= 0;
        }
      }
      
      return true;
    });
  }, [stockItems, lowStockOnly, outOfStockOnly]);

  const outOfStockCount = dashboardData?.stock_summary.out_of_stock_products ?? 0;
  const tabLabel = (icon: React.ReactNode, text: string) => (
    <span>
      {icon}
      {!isMobile ? <> {text}</> : null}
    </span>
  );
  const showHeaderTransferButton = activeTab === 'transfers' && canCreateTransfer;
  const showHeaderAdjustmentButton = activeTab !== 'transfers' && canCreateAdjustment;

  return (
    <div className="inventory-page">
      <div className="inventory-header">
        <div className="inventory-header__title-wrap">
          <div className="inventory-header__icon"><InboxOutlined /></div>
          <div>
            <Title level={2} className="inventory-header__title">Inventory Management</Title>
            <Text className="inventory-header__subtitle">Stock control, movements & alerts</Text>
          </div>
        </div>
        <Space>
          {showHeaderAdjustmentButton && (
            <Button className="inventory-stock-adjust-btn" type="primary" icon={<PlusOutlined />} onClick={openAdjustmentModal}>
              Stock Adjustment
            </Button>
          )}
          {showHeaderTransferButton && (
            <Button className="inventory-stock-adjust-btn" type="primary" icon={<PlusOutlined />} onClick={openTransferModal}>
              New Transfer
            </Button>
          )}
        </Space>
      </div>

      {/* Summary Cards (hidden on Adjustments tab to avoid duplicate card sections) */}
      {activeTab !== 'adjustments' && (
      <Row gutter={[16, 16]} className="inventory-stats-row">
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card hoverable onClick={handleTotalProductsClick} className={`inv-stat inv-stat--blue ${!lowStockOnly && !outOfStockOnly && !expiryFilter ? 'active' : ''}`}>
            <div className="inv-stat__top" />
            <div className="inv-stat__label">Total Products</div>
            <div className="inv-stat__value inv-stat__value--blue">{dashboardData?.stock_summary.total_products || 0}</div>
            <div className="inv-stat__sub">{dashboardStockScopeLabel}</div>
            <div className="inv-stat__icon"><InboxOutlined /></div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card className="inv-stat inv-stat--green">
            <div className="inv-stat__top" />
            <div className="inv-stat__label">Total Stock Units</div>
            <div className="inv-stat__value inv-stat__value--green">{dashboardData?.stock_summary.total_stock_units || 0}</div>
            <div className="inv-stat__sub">{dashboardStockScopeLabel}</div>
            <div className="inv-stat__icon"><StockOutlined /></div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card hoverable onClick={handleLowStockClick} className={`inv-stat inv-stat--amber ${lowStockOnly ? 'active' : ''}`}>
            <div className="inv-stat__top" />
            <div className="inv-stat__label">Low Stock Items</div>
            <div className="inv-stat__value inv-stat__value--amber">{dashboardData?.stock_summary.low_stock_products || 0}</div>
            <div className="inv-stat__sub">Needs reordering · {dashboardStockScopeLabel}</div>
            <div className="inv-stat__icon"><WarningOutlined /></div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card hoverable onClick={handleOutOfStockClick} className={`inv-stat inv-stat--red ${outOfStockOnly ? 'active' : ''}`}>
            <div className="inv-stat__top" />
            <div className="inv-stat__label">Out of Stock</div>
            <div className="inv-stat__value inv-stat__value--red">{dashboardData?.stock_summary.out_of_stock_products || 0}</div>
            <div className="inv-stat__sub">Action needed · {dashboardStockScopeLabel}</div>
            <div className="inv-stat__icon"><CloseCircleOutlined /></div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card hoverable onClick={() => handleExpiryClick('expiring_soon')} className={`inv-stat inv-stat--amber ${expiryFilter === 'expiring_soon' ? 'active' : ''}`}>
            <div className="inv-stat__top" />
            <div className="inv-stat__label">Expiring soon</div>
            <div className="inv-stat__value inv-stat__value--amber">{dashboardData?.stock_summary.expiring_soon_products || 0}</div>
            <div className="inv-stat__sub">Batch expires within 30 days · {dashboardStockScopeLabel}</div>
            <div className="inv-stat__icon"><AlertOutlined /></div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <Card hoverable onClick={() => handleExpiryClick('expired')} className={`inv-stat inv-stat--red ${expiryFilter === 'expired' ? 'active' : ''}`}>
            <div className="inv-stat__top" />
            <div className="inv-stat__label">Expired</div>
            <div className="inv-stat__value inv-stat__value--red">{dashboardData?.stock_summary.expired_products || 0}</div>
            <div className="inv-stat__sub">Segregate from sale · {dashboardStockScopeLabel}</div>
            <div className="inv-stat__icon"><CloseCircleOutlined /></div>
          </Card>
        </Col>
      </Row>
      )}

      {/* Alerts */}
      {outOfStockCount > 0 && (
        <Alert
          message={`${outOfStockCount} product${outOfStockCount > 1 ? 's are' : ' is'} out of stock — immediate restocking required`}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          closable
        />
      )}
      {(dashboardData?.stock_summary.expired_products || 0) > 0 && (
        <Alert
          message={`${dashboardData?.stock_summary.expired_products} expired product(s) in stock — segregate and do not sell`}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          action={
            <Button size="small" onClick={() => handleExpiryClick('expired')}>
              View expired
            </Button>
          }
        />
      )}
      {(dashboardData?.stock_summary.expiring_soon_products || 0) > 0 && (
        <Alert
          message={`${dashboardData?.stock_summary.expiring_soon_products} product(s) expiring soon — sell or return first`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          action={
            <Button size="small" onClick={() => handleExpiryClick('expiring_soon')}>
              View expiring
            </Button>
          }
        />
      )}

      {activeTab === 'adjustments' && (
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} lg={8} xl={4}>
            <Card className="adj-stat-card">
              <div className="adj-stat-card__label">Total Adjustments</div>
              <div className="adj-stat-card__value">{adjustmentStats.total}</div>
              <div className="adj-stat-card__sub">All locations</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8} xl={4}>
            <Card className="adj-stat-card">
              <div className="adj-stat-card__label">Approved</div>
              <div className="adj-stat-card__value">{adjustmentStats.approved}</div>
              <div className="adj-stat-card__sub">This view</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8} xl={4}>
            <Card className="adj-stat-card">
              <div className="adj-stat-card__label">Pending</div>
              <div className="adj-stat-card__value">{adjustmentStats.pending}</div>
              <div className="adj-stat-card__sub">Awaiting review</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8} xl={4}>
            <Card className="adj-stat-card">
              <div className="adj-stat-card__label">Cancelled</div>
              <div className="adj-stat-card__value">{adjustmentStats.cancelled}</div>
              <div className="adj-stat-card__sub">This view</div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Tabs - grows to fill remaining viewport */}
      <div className="inventory-tabs-wrapper">
      <Card className="inventory-tabs-card" bodyStyle={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Tabs
          className="inventory-tabs"
          activeKey={activeTab}
          onChange={onInventoryTabChange}
          animated={false}
          destroyInactiveTabPane={false}
          items={[
            {
              key: 'stock',
              label: tabLabel(<StockOutlined />, 'Stock Levels'),
              children: (
                <div className="inventory-stock-tab-content">
                  {(lowStockOnly || outOfStockOnly || expiryFilter) && (
                    <Alert
                      message={
                        expiryFilter === 'expired'
                          ? `Showing expired items (${filteredStockItems.length} on this page)`
                          : expiryFilter === 'expiring_soon'
                            ? `Showing items expiring soon (${filteredStockItems.length} on this page)`
                            : lowStockOnly
                              ? `Showing only low stock items (${filteredStockItems.length} items)`
                              : `Showing only out of stock items (${filteredStockItems.length} items)`
                      }
                      type="info"
                      showIcon
                      closable
                      onClose={() => {
                        setLowStockOnly(false);
                        setOutOfStockOnly(false);
                        setExpiryFilter(null);
                        setStockPagination(prev => ({ ...prev, current: 1 }));
                      }}
                      style={{ marginBottom: 16 }}
                    />
                  )}
                  <Space className="inventory-stock-toolbar" style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
                    <Input.Search
                      className="app-search"
                      placeholder="Search products..."
                      allowClear
                      size="large"
                      enterButton
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onSearch={(q) => setSearchText(q || '')}
                      style={{ width: 300 }}
                    />
                    <Space>
                      <Text type="secondary" style={{ marginRight: 8 }}>
                        {filteredStockItems.length} on this page · {stockPagination.total} total
                        {debouncedSearch ? ` matching “${debouncedSearch}”` : ''}
                      </Text>
                      {canExportInventory && (
                        <Button
                          type="primary"
                          icon={<DownloadOutlined />}
                          onClick={() => void exportStockToExcel()}
                          loading={exportingStock}
                        >
                          Export Excel
                        </Button>
                      )}
                    </Space>
                  </Space>
                  <Table
                    columns={stockColumns}
                    dataSource={filteredStockItems}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                      current: stockPagination.current,
                      pageSize: stockPagination.pageSize,
                      total: outOfStockOnly ? filteredStockItems.length : stockPagination.total,
                      showSizeChanger: true,
                      showTotal: (total, range) => {
                        if (outOfStockOnly) {
                          return `Showing ${total} out of stock (this page)`;
                        }
                        if (lowStockOnly) {
                          return `${range[0]}-${range[1]} of ${total} low-stock rows`;
                        }
                        if (debouncedSearch) {
                          return `${range[0]}-${range[1]} of ${total} matching “${debouncedSearch}”`;
                        }
                        return `${range[0]}-${range[1]} of ${total} items`;
                      },
                      onChange: (page, pageSize) => {
                        setStockPagination(prev => ({ ...prev, current: page, pageSize }));
                      },
                    }}
                    scroll={{
                      x: isMobile ? 1000 : 1200,
                      y: tableScrollY,
                    }}
                    size={isMobile ? 'small' : 'middle'}
                  />
                </div>
              ),
            },
            {
              key: 'movements',
              label: tabLabel(<ArrowUpOutlined />, 'Stock Movements'),
              children: (
                <Table
                  columns={movementColumns}
                  dataSource={recentMovements}
                  rowKey="id"
                  loading={loading}
                  pagination={{
                    current: movementPagination.current,
                    pageSize: movementPagination.pageSize,
                    total: movementPagination.total,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} movements`,
                    onChange: (page, pageSize) => {
                      setMovementPagination(prev => ({ ...prev, current: page, pageSize }));
                    },
                  }}
                  scroll={{
                    x: isMobile ? 1000 : 1200,
                    y: tableScrollY,
                  }}
                  size={isMobile ? 'small' : 'middle'}
                />
              ),
            },
            {
              key: 'adjustments',
              label: tabLabel(<CheckCircleOutlined />, 'Adjustments'),
              children: (
                <div>
                  {RBACService.isSuperAdmin() && (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message="Platform view: approvals are done by each company’s Admin. Super Admin cannot approve tenant stock adjustments."
                    />
                  )}
                  <Space
                    className="adjustments-toolbar"
                    style={{
                      marginTop: 12,
                      marginBottom: 16,
                      width: '100%',
                      justifyContent: isMobile ? 'flex-start' : 'space-between',
                    }}
                    direction={isMobile ? 'vertical' : 'horizontal'}
                    size="small"
                  >
                    <Text type="secondary" style={{ width: isMobile ? '100%' : undefined }}>
                      IN/OUT adjustments with pending and approval workflow
                    </Text>
                    <Space
                      style={{
                        width: isMobile ? '100%' : undefined,
                        marginRight: isMobile ? 0 : 12,
                      }}
                    >
                      <Select
                        className="adjustment-status-select"
                        value={adjustmentStatusFilter}
                        style={{ width: isMobile ? '100%' : 180 }}
                        suffixIcon={<DownOutlined style={{ color: '#111827', fontSize: 14 }} />}
                        onChange={(v) => {
                          setAdjustmentStatusFilter(v);
                          setAdjustmentPagination((prev) => ({ ...prev, current: 1 }));
                        }}
                      >
                        <Option value="ALL">All statuses</Option>
                        <Option value="PENDING">Pending</Option>
                        <Option value="APPROVED">Approved</Option>
                        <Option value="CANCELLED">Cancelled</Option>
                      </Select>
                    </Space>
                  </Space>
                  <Table
                    columns={adjustmentColumns}
                    dataSource={adjustments}
                    rowKey="id"
                    className="adjustments-table"
                    loading={loading}
                    pagination={{
                      current: adjustmentPagination.current,
                      pageSize: adjustmentPagination.pageSize,
                      total: adjustmentPagination.total,
                      showSizeChanger: true,
                      showTotal: (total) => `Showing ${adjustments.length} rows · approx total ${total}`,
                      onChange: (page, pageSize) => {
                        setAdjustmentPagination((prev) => ({ ...prev, current: page, pageSize }));
                      },
                    }}
                    scroll={{
                      x: isMobile ? 1000 : 1200,
                      y: tableScrollY,
                    }}
                    size={isMobile ? 'small' : 'middle'}
                  />
                </div>
              ),
            },
            {
              key: 'alerts',
              label: (
                <span>
                  <AlertOutlined />
                  {!isMobile ? <> Low Stock Alerts</> : null}
                  {lowStockAlerts.length > 0 && (
                    <Badge count={lowStockAlerts.length} style={{ marginLeft: 8 }} />
                  )}
                </span>
              ),
              children: (
                <div>
                  <Alert
                    message={`${lowStockAlerts.length} products need reordering`}
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  <Table
                    columns={alertColumns}
                    dataSource={lowStockAlerts}
                    rowKey={(record) => `${record.product.id}-${record.location.id}`}
                    loading={loading}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showTotal: (total) => `Total ${total} alerts`,
                    }}
                    scroll={{
                      x: isMobile ? 1000 : 1200,
                      y: tableScrollY,
                    }}
                    size={isMobile ? 'small' : 'middle'}
                  />
                </div>
              ),
            },
            {
              key: 'transfers',
              label: tabLabel(<SwapOutlined />, 'Transfers'),
              children: (
                <div>
                  <Space
                    className="adjustments-toolbar"
                    style={{
                      marginTop: 12,
                      marginBottom: 16,
                      width: '100%',
                      justifyContent: 'flex-start',
                    }}
                    direction={isMobile ? 'vertical' : 'horizontal'}
                    size="small"
                  >
                    <Text type="secondary" style={{ width: isMobile ? '100%' : undefined }}>
                      Move stock from one location to another with receive confirmation.
                    </Text>
                  </Space>
                  <Table
                    columns={transferColumns}
                    dataSource={transfers}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                      current: transferPagination.current,
                      pageSize: transferPagination.pageSize,
                      total: transferPagination.total,
                      showSizeChanger: true,
                      showTotal: (total) => `Showing ${transfers.length} rows · approx total ${total}`,
                      onChange: (page, pageSize) => {
                        setTransferPagination((prev) => ({ ...prev, current: page, pageSize }));
                      },
                    }}
                    scroll={{
                      x: isMobile ? 1000 : 1200,
                      y: tableScrollY,
                    }}
                    size={isMobile ? 'small' : 'middle'}
                  />
                </div>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title="Edit Selling Price"
        open={!!editStockItem}
        onCancel={() => setEditStockItem(null)}
        onOk={handleSaveSellingPrice}
        confirmLoading={editSaving}
        okText="Save"
        destroyOnClose
      >
        {editStockItem && (
          <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item label="Product" style={{ marginBottom: 8 }}>
              <Text strong>{editStockItem.product.name}</Text>
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>Code: {editStockItem.product.code}</div>
            </Form.Item>
            <Form.Item label="Cost Price" style={{ marginBottom: 16 }}>
              <Text>Nu. {(editStockItem.product.cost_price ?? 0).toLocaleString()}</Text>
            </Form.Item>
            <Form.Item
              name="selling_price"
              label="Selling Price (Nu.)"
              rules={[{ required: true, message: 'Required' }, { type: 'number', min: 0, message: 'Must be ≥ 0' }]}
            >
              <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
            </Form.Item>
          </Form>
        )}
      </Modal>

      <ModernFormModal
        open={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setDetailAdjustment(null);
        }}
        title={detailAdjustment ? `Adjustment ${detailAdjustment.adjustment_number}` : 'Adjustment details'}
        subtitle="Review adjustment details, reason, and item-level changes."
        headerVariant="light"
        getContainer={() => document.querySelector('.main-content-full') as HTMLElement || document.body}
        wrapClassName="form-panel-full-wrap adjustment-detail-modal-wrap"
        width="100%"
        style={{ maxWidth: 'none' }}
        bodyStyle={{ padding: 0 }}
        cancelText="Close"
        showOkButton={false}
        showFooter={true}
        isSubmitting={false}
        destroyOnClose
      >
        {detailAdjustment && (
          <div style={{ padding: 24, width: '100%', boxSizing: 'border-box', display: 'grid', gap: 10 }}>
            <div><b>Status:</b> {detailAdjustment.status}</div>
            <div><b>Date:</b> {detailAdjustment.adjustment_date ? moment(detailAdjustment.adjustment_date).format('MMM DD, YYYY') : '—'}</div>
            <div><b>Location:</b> {locations.find((l) => l.id === detailAdjustment.location_id)?.name || `#${detailAdjustment.location_id}`}</div>
            <div><b>Reason:</b> {detailAdjustment.reason || '—'}</div>
            <div><b>Notes:</b> {detailAdjustment.notes || '—'}</div>
            <div>
              <b>Items:</b>
              <div style={{ marginTop: 6 }}>
                {(detailAdjustment.adjustment_items || []).length === 0 ? (
                  <div style={{ color: '#6b7280' }}>No items</div>
                ) : (
                  (detailAdjustment.adjustment_items || []).map((it) => (
                    <div key={it.id || `${detailAdjustment.id}-${it.product_id}`} style={{ padding: '6px 0' }}>
                      {(it.product?.name || detailProductNames[it.product_id] || `Product #${it.product_id}`)}: {it.current_quantity} → {it.adjusted_quantity}
                      <span style={{ marginLeft: 8, color: it.difference >= 0 ? '#15803d' : '#b91c1c' }}>
                        ({it.difference >= 0 ? '+' : ''}{it.difference})
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </ModernFormModal>

      <ModernFormModal
        open={adjustmentModalOpen}
        onClose={() => {
          if (adjustmentSubmitting) return;
          setAdjustmentModalOpen(false);
        }}
        title="Create Stock Adjustment"
        subtitle="Add or remove stock quantity for an existing product."
        headerVariant="light"
        getContainer={() => document.querySelector('.main-content-full') as HTMLElement || document.body}
        wrapClassName="form-panel-full-wrap"
        width="100%"
        style={{ maxWidth: 'none' }}
        bodyStyle={{ padding: 0 }}
        cancelText="Cancel"
        okText="Create Adjustment"
        onOk={() => adjustmentForm.submit()}
        isSubmitting={adjustmentSubmitting}
        destroyOnClose
      >
        <div style={{ padding: 24, width: '100%', boxSizing: 'border-box' }}>
          <Form
            form={adjustmentForm}
            layout="vertical"
            initialValues={{ adjustment_type: 'IN', quantity: 1 }}
            onFinish={handleCreateAdjustment}
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="location_id"
                  label="Location"
                  rules={[{ required: true, message: 'Please select location' }]}
                >
                  <Select
                    placeholder="Select location"
                    showSearch
                    optionFilterProp="children"
                    className="centered-select-arrow"
                    onChange={() => {
                      adjustmentForm.setFieldsValue({ product_id: undefined });
                      setAdjustmentCurrentQty(null);
                    }}
                  >
                    {scopedLocations.map((loc) => (
                      <Option key={loc.id} value={loc.id}>
                        {loc.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="product_id"
                  label="Product"
                  tooltip="Only products that already have inventory at the selected location (matches the stock list)."
                  rules={[{ required: true, message: 'Please select product' }]}
                >
                  <Select
                    placeholder={
                      adjustmentLocationId ? 'Select product at this location' : 'Select a location first'
                    }
                    showSearch
                    optionFilterProp="children"
                    className="centered-select-arrow"
                    loading={adjustmentProductsLoading}
                    disabled={!adjustmentLocationId}
                    notFoundContent={
                      adjustmentProductsLoading
                        ? 'Loading…'
                        : adjustmentLocationId
                          ? 'No inventory rows at this location yet'
                          : 'Select a location first'
                    }
                  >
                    {adjustmentProducts.map((p) => (
                      <Option key={p.id} value={p.id}>
                        {p.name} ({p.code})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {adjustmentProductId && adjustmentLocationId ? (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              message={
                adjustmentCurrentQtyLoading
                  ? 'Checking current stock...'
                  : `Current stock: ${adjustmentCurrentQty ?? getCurrentQuantity(Number(adjustmentProductId), Number(adjustmentLocationId))}`
              }
              />
            ) : null}

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="adjustment_type"
                  label="Adjustment Type"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Select className="centered-select-arrow">
                    <Option value="IN">IN (Stock Received)</Option>
                    <Option value="OUT">OUT (Stock Reduction)</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="quantity"
                  label="Quantity"
                  rules={[{ required: true, message: 'Please enter quantity' }]}
                >
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            {adjustmentType !== 'OUT' ? (
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="manufacture_date"
                    label="Manufacture date"
                    rules={[{ required: true, message: 'Enter manufacture date' }]}
                  >
                    <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="expiry_date"
                    label="Expiry date"
                    dependencies={['manufacture_date']}
                    rules={[
                      { required: true, message: 'Enter expiry date' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const mfg = getFieldValue('manufacture_date');
                          if (!value || !mfg) return Promise.resolve();
                          if (moment(value).isAfter(moment(mfg), 'day')) return Promise.resolve();
                          return Promise.reject(new Error('Expiry must be after manufacture date'));
                        },
                      }),
                    ]}
                  >
                    <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
                  </Form.Item>
                </Col>
              </Row>
            ) : null}

            <Form.Item
              name="reason"
              label="Reason"
              rules={[{ required: true, message: 'Please enter reason' }]}
            >
              <Input placeholder="e.g. Supplier delivery / Damage / Count correction" />
            </Form.Item>

            <Form.Item name="notes" label="Notes (optional)">
              <Input.TextArea rows={3} placeholder="Additional details" />
            </Form.Item>
          </Form>
        </div>
      </ModernFormModal>
      <ModernFormModal
        open={transferModalOpen}
        onClose={() => {
          if (transferSubmitting) return;
          setTransferModalOpen(false);
        }}
        title="Create Stock Transfer"
        subtitle="Transfer items from source location to destination location."
        headerVariant="light"
        getContainer={() => document.querySelector('.main-content-full') as HTMLElement || document.body}
        wrapClassName="form-panel-full-wrap"
        width="100%"
        style={{ maxWidth: 'none' }}
        bodyStyle={{ padding: 0 }}
        cancelText="Cancel"
        okText="Create Transfer"
        onOk={() => transferForm.submit()}
        isSubmitting={transferSubmitting}
        destroyOnClose
      >
        <div style={{ padding: 24, width: '100%', boxSizing: 'border-box' }}>
          <Form
            form={transferForm}
            layout="vertical"
            initialValues={{ transfer_type: 'BRANCH_TRANSFER', lines: [{ quantity: 1 }] }}
            onFinish={handleCreateTransfer}
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="from_location_id"
                  label="From Location"
                  rules={[{ required: true, message: 'Please select source location' }]}
                >
                  <Select
                    placeholder="Select source location"
                    showSearch
                    optionFilterProp="children"
                    className="centered-select-arrow"
                    onChange={() => {
                      transferForm.setFieldsValue({ lines: [{ quantity: 1 }] });
                    }}
                  >
                    {scopedLocations.map((loc) => (
                      <Option key={loc.id} value={loc.id}>
                        {loc.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="to_location_id"
                  label="To Location"
                  rules={[{ required: true, message: 'Please select destination location' }]}
                >
                  <Select placeholder="Select destination location" showSearch optionFilterProp="children" className="centered-select-arrow">
                    {locations
                      .filter((loc) => Number(loc.id) !== Number(transferFromLocationId))
                      .map((loc) => (
                        <Option key={loc.id} value={loc.id}>
                          {loc.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Products to transfer
            </Text>
            <Form.List name="lines">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <Row key={field.key} gutter={[8, 8]} align="top" style={{ marginBottom: 4 }}>
                      <Col xs={24} md={9}>
                        <Form.Item
                          name={[field.name, 'product_id']}
                          fieldKey={[field.fieldKey ?? field.key ?? field.name, 'product_id']}
                          label={field.name === 0 ? 'Product' : ' '}
                          rules={[{ required: true, message: 'Select product' }]}
                        >
                          <Select
                            placeholder={
                              transferFromLocationId ? 'Select product' : 'Select source location first'
                            }
                            showSearch
                            optionFilterProp="children"
                            className="centered-select-arrow"
                            loading={transferProductsLoading}
                            disabled={!transferFromLocationId}
                            notFoundContent={
                              transferProductsLoading
                                ? 'Loading…'
                                : transferFromLocationId
                                  ? 'No products at this location'
                                  : 'Select source location first'
                            }
                          >
                            {transferProducts.map((p) => (
                              <Option key={p.id} value={p.id}>
                                {p.name} ({p.code})
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={5}>
                        <Form.Item
                          name={[field.name, 'quantity']}
                          fieldKey={[field.fieldKey ?? field.key ?? field.name, 'quantity']}
                          label={field.name === 0 ? 'Qty' : ' '}
                          rules={[
                            { required: true, message: 'Qty' },
                            { type: 'number', min: 1, message: 'Min 1' },
                          ]}
                        >
                          <InputNumber min={1} style={{ width: '100%' }} placeholder="Qty" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item
                          name={[field.name, 'line_notes']}
                          fieldKey={[field.fieldKey ?? field.key ?? field.name, 'line_notes']}
                          label={field.name === 0 ? 'Line note (optional)' : ' '}
                        >
                          <Input placeholder="Per-line note" />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={2} style={{ display: 'flex', alignItems: field.name === 0 ? 'flex-end' : 'center', paddingBottom: field.name === 0 ? 24 : 0 }}>
                        {fields.length > 1 ? (
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => remove(field.name)}
                            aria-label="Remove line"
                          />
                        ) : null}
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add({ quantity: 1 })}
                      block
                      icon={<PlusOutlined />}
                      disabled={!transferFromLocationId}
                    >
                      Add another product
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            {transferFromLocationId ? (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Availability is checked when you create the transfer. Each product can appear on one row only; use quantity for multiples of the same SKU."
              />
            ) : null}

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="transfer_type"
                  label="Transfer Type"
                  rules={[{ required: true, message: 'Please select transfer type' }]}
                >
                  <Select className="centered-select-arrow">
                    <Option value="BRANCH_TRANSFER">Branch Transfer</Option>
                    <Option value="DAMAGE">Damage</Option>
                    <Option value="DEFECT">Defect</Option>
                    <Option value="RECALL">Recall</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="notes" label="Notes (optional)">
              <Input.TextArea rows={3} placeholder="Additional transfer details" />
            </Form.Item>
          </Form>
        </div>
      </ModernFormModal>
      <Modal
        title={editingTransfer ? `Edit Transfer ${editingTransfer.transfer_number}` : 'Edit Transfer'}
        open={editTransferModalOpen}
        onCancel={() => {
          if (editTransferSubmitting) return;
          setEditTransferModalOpen(false);
          setEditingTransfer(null);
        }}
        onOk={handleSaveTransferEdit}
        confirmLoading={editTransferSubmitting}
        okText="Save"
        destroyOnClose
      >
        <Form form={editTransferForm} layout="vertical" style={{ marginTop: 12 }}>
          {editingTransfer ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Line quantities"
              description="Higher quantities take more stock from the sending location; lower quantities return stock there. The server checks available quantity."
            />
          ) : null}
          <Form.List name="transfer_lines">
            {(fields) => (
              <div style={{ marginBottom: 8 }}>
                {fields.map((field) => (
                  <Row key={field.key} gutter={12} align="top" style={{ marginBottom: 8 }}>
                    <Form.Item
                      name={[field.name, 'item_id']}
                      fieldKey={[field.fieldKey ?? field.key ?? field.name, 'item_id']}
                      style={{ display: 'none' }}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, 'product_id']}
                      fieldKey={[field.fieldKey ?? field.key ?? field.name, 'product_id']}
                      style={{ display: 'none' }}
                    >
                      <Input />
                    </Form.Item>
                    <Col xs={24} sm={14}>
                      <Form.Item
                        name={[field.name, 'product_label']}
                        fieldKey={[field.fieldKey ?? field.key ?? field.name, 'product_label']}
                        label={field.name === 0 ? 'Product' : ''}
                      >
                        <Input readOnly bordered={false} style={{ paddingLeft: 0 }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={10}>
                      <Form.Item
                        name={[field.name, 'quantity']}
                        fieldKey={[field.fieldKey ?? field.key ?? field.name, 'quantity']}
                        label={field.name === 0 ? 'Quantity' : ''}
                        rules={[
                          { required: true, message: 'Enter quantity' },
                          { type: 'number', min: 1, message: 'Min 1' },
                        ]}
                      >
                        <InputNumber min={1} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                ))}
              </div>
            )}
          </Form.List>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={4} placeholder="Update transfer notes" />
          </Form.Item>
        </Form>
      </Modal>
      </div>
    </div>
  );
};

export default Inventory;
